import React, { useState } from 'react';
import _noop from 'lodash/noop';
import {
  Attachment,
  ConditionAttachment,
  CommunicationAttachment,
  DocumentReferenceAttachment,
  isCondition,
  isDocumentReference,
  isMedicationRequest,
  isObservation,
  isFhirReference,
  MedicationRequestAttachment,
  ObservationAttachment,
  PatientAttachment,
  QuestionnaireResponseAttachment,
  ReferenceOrResource,
} from './Attachment';
import RangeViewModalV2 from '../../Measurements/Graphs/Ranges/RangeViewModalV2';
import { DocumentReferenceModal } from './modals/DocumentReferenceModal';
import { SurveyViewModal } from './modals/SurveyViewModal';
import { PatientViewModal } from './modals/PatientRecordModal';
import { ObservationViewModal } from './modals/ObservationViewModal';
import { UnknownAttachmentModal } from './modals/UnknownAttachmentModal';
import { CommunicationViewModal } from './modals/CommunicationViewModal';
import { ConditionViewModal } from './modals/ConditionViewModal';
import { MedicationRequestViewModal } from './modals/MedicationRequestViewModal';
import { useAttachmentArbiterState } from './useAttachmentArbiterState';
import FileService from '../../../../../services/FileService';
import { useService } from '../../../../../hooks';

type ContextValue = {
  loading: boolean;
  attachmentMap: Record<string, Attachment>;
  openAttachment: (
    reference: ReferenceOrResource,
    options?: { disableDeepLink?: boolean },
  ) => void;
  addAttachment: (reference: string, attachment: Attachment) => void;
};

const AttachmentArbiterContext = React.createContext<ContextValue>({
  loading: true,
  attachmentMap: {},
  openAttachment: _noop,
  addAttachment: _noop,
});

type OpenAttachment = {
  attachment: Attachment;
  reference: ReferenceOrResource;
  options?: { disableDeepLink?: boolean };
};

const AttachmentModal = ({
  patient,
  openAttachment,
  onDismiss,
}: {
  patient: fhir.Patient;
  openAttachment?: OpenAttachment;
  onDismiss: () => void;
}) => {
  if (!openAttachment) return null;

  const { attachment } = openAttachment ?? {};

  const props = {
    ...openAttachment,
    ...openAttachment?.options,
    onDismiss,
    openInNewTab: true,
  };

  if (attachment instanceof DocumentReferenceAttachment) {
    return <DocumentReferenceModal {...props} attachment={attachment} />;
  } else if (
    attachment instanceof CommunicationAttachment &&
    isFhirReference(props.reference)
  ) {
    return <CommunicationViewModal {...props} reference={props.reference} />;
  } else if (attachment instanceof QuestionnaireResponseAttachment) {
    return <SurveyViewModal {...props} attachment={attachment} />;
  } else if (attachment instanceof ConditionAttachment) {
    return <ConditionViewModal {...props} attachment={attachment} />;
  } else if (attachment instanceof MedicationRequestAttachment) {
    return <MedicationRequestViewModal {...props} attachment={attachment} />;
  } else if (attachment instanceof PatientAttachment) {
    return <PatientViewModal {...props} attachment={attachment} />;
  } else if (attachment instanceof ObservationAttachment) {
    if (attachment.isBiomarker && attachment.primaryCode) {
      return (
        <RangeViewModalV2
          dimension={{
            codes: [attachment.primaryCode],
          }}
          isOpen={true}
          onClose={onDismiss}
          patient={patient}
          initialSelectedResourceId={attachment?.resource.id}
          reviewAnalyticsSurface="ai_tools"
        />
      );
    }

    return <ObservationViewModal {...props} attachment={attachment} />;
  }

  return <UnknownAttachmentModal {...props} />;
};

const fileService = new FileService();

export const AttachmentArbiterProvider = ({
  children,
  attachments,
  patient,
}: {
  children: React.ReactNode;
  attachments?: string[];
  patient: fhir.Patient;
}) => {
  const state = useAttachmentArbiterState(attachments);
  const [openAttachment, setOpenAttachment] = useState<{
    attachment: Attachment;
    reference: ReferenceOrResource;
    options?: { disableDeepLink?: boolean };
  }>();

  const [fetchOpenLink] = useService(fileService.fetchOpenLink);

  const handleOpenPdf = React.useCallback(
    async (fileId: string) => {
      try {
        const result = await fetchOpenLink(fileId);
        const downloadUrl = result?.data?.downloadUrl;
        if (!downloadUrl) throw new Error('No open url for file');
        window.open(downloadUrl, '_blank');
      } catch (e) {
        console.error(e);
      }
    },
    [fetchOpenLink],
  );

  const handleOpen: ContextValue['openAttachment'] = (reference, options) => {
    if (isFhirReference(reference)) {
      const referenceId = reference.reference as string;
      const attachment = state.attachmentMap[referenceId];
      setOpenAttachment({ attachment, reference, options });
    } else if (isDocumentReference(reference)) {
      // if a raw document reference is passed in, we need to get the file id from the identifier
      // because it's not in the attachment map (e.g., a document reference outside the impression)
      const maybeFileId = reference.identifier?.[0].value;
      if (maybeFileId) {
        handleOpenPdf(maybeFileId);
      }
    } else if (isObservation(reference)) {
      setOpenAttachment({
        attachment: new ObservationAttachment(reference),
        reference,
        options,
      });
    } else if (isCondition(reference)) {
      setOpenAttachment({
        attachment: new ConditionAttachment(reference),
        reference,
        options,
      });
    } else if (isMedicationRequest(reference)) {
      setOpenAttachment({
        attachment: new MedicationRequestAttachment(reference),
        reference,
        options,
      });
    }
  };

  const addAttachment = (reference: string, attachment: Attachment) => {
    state.setAttachmentMap(prev => ({ ...prev, [reference]: attachment }));
  };

  return (
    <AttachmentArbiterContext.Provider
      value={{
        ...state,
        openAttachment: handleOpen,
        addAttachment,
      }}
    >
      {children}

      <AttachmentModal
        patient={patient}
        openAttachment={openAttachment}
        onDismiss={() => setOpenAttachment(undefined)}
      />
    </AttachmentArbiterContext.Provider>
  );
};

export const useAttachmentArbiter = () =>
  React.useContext(AttachmentArbiterContext);
